import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import {
  SelectedAccountManager,
  SelectedAccountManagerService
} from 'core/selected-account-manager/selected-account-manager.service';
import { MatDialog } from '@angular/material/dialog';
import { ActiveTab, ActiveTabIndex, getActiveTabIndex } from 'src/app/modules/shared/entities/active-tab.enum';
import { ValueLabel } from 'src/app/modules/shared/entities/value-label';
import { ImportItemsQuoteService } from 'src/app/modules/shared/import-quotes-and-orders/import-items-quote.service';
import { TouchpointsService } from '../../../crm/touchpoints/services/touchpoints.service';
import { AccountManager } from '../../../shared/entities/account-manager.class';
import { StorageValues } from '../../../shared/entities/storage-values.enum';
import { QuoteAdvancedSearchComponent } from '../quote-advanced-search/quote-advanced-search.component';
import { QuoteDetailsService } from '../services/quote-details.service';

@Component({
  selector: 'app-quotes-subsections-filter-form',
  templateUrl: './quotes-subsections-filter-form.component.html',
  styleUrls: ['./quotes-subsections-filter-form.component.scss'],
  standalone: false
})
export class QuotesSubsectionsFilterFormComponent implements OnInit, OnDestroy, OnChanges {
  subs = new Subscription();
  @Input() templateValuesType;
  @Input() getListMethod: (_, __, ___) => void;
  @Input() label: string;
  @Input() activeTab: string;
  @Input() sortKey: string;
  @Input() isOnAccountsPage: boolean;
  @Input() bpNumber: string;
  @Input() lnOrderTemplates;

  sectionSearchByOptions: ValueLabel[] = [];
  sectionShowForOptions: ValueLabel[] = [];
  lnOrderStatusOptions: ValueLabel[] = [];
  today = new Date();
  currentSearchText = '';
  prettyStartDateValue = 'Start Date';
  prettyEndDateValue = 'End Date';
  prettyStartDate = this.prettyStartDateValue;
  prettyEndDate = this.prettyEndDateValue;
  valuesFormDialog: any = {};
  searchFormFields = {
    displayedSearchValue: '',
    quotesShowFor: 1,
    ordersShowFor: 1,
    lnOrderStatus: 0,
    startDate: null,
    endDate: null
  };

  allAM = {
    name: 'All',
    employeeId: '0'
  };

  cachedSearchValues: any;
  cachedSort: string;
  amList: AccountManager[];
  filteredAMList: AccountManager[];
  defaultAccountManager: any;
  currentAccountManager: AccountManager;
  oldAccountManager: AccountManager;
  private subscription = new Subscription();
  sort: Sort;
  searchForm = this.fb.group(this.searchFormFields);
  clearFiltersNeeded = false;
  appStrings = this.appInfoService.appStrings;
  isOnline: boolean;
  electronAppOutOfSync = false;
  activeTabName = ActiveTab;
  isNSLocaleSelected: boolean;
  importQuoteActive = false;

  mobileToggleValues = [];
  activeTabIndex: ActiveTabIndex;
  toggleValues = [];

  @ViewChild('searchFieldMobile') searchFieldMobile: any;

  @Output() resetPage = new EventEmitter();
  @Output() resetSort = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private touchpointsService: TouchpointsService,
    private quoteDetailsService: QuoteDetailsService,
    private dialog: MatDialog,
    private deviceService: DeviceDetectorService,
    private router: Router,
    private importItemsQuoteService: ImportItemsQuoteService,
    private selectedAccountManagerService: SelectedAccountManagerService
  ) { }

  ngOnInit() {
    this.isOnline = this.appStateService.state.isOnline;
    this.isNSLocaleSelected = this.appInfoService.isNSLocaleSelected();
    this.electronAppOutOfSync = this.appStateService.checkIfElectronAppIsOutOfSync();
    this.quoteDetailsService.getAccountManagersList().subscribe(result => this.amList = [this.allAM, ...result]);
    this.activeTabIndex = getActiveTabIndex(this.activeTab);
    this.setFormOptions();
    this.setupMobileToggleButtons();
    this.setupToggleButtons();
    this.initList();
    this.importQuoteActive = !!this.importItemsQuoteService.getDestinationQuoteId();
  }

  ngOnChanges() {
    this.setFormOptions();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Opens the pop-up dialog and sends certain data to be used within the form that's in the dialog
   */
  openAdvancedFilteringDialog() {
    const advancedFilteringDialog = this.dialog.open(QuoteAdvancedSearchComponent, {
      data: {
        templateValues: {
          lnStatuses: this.lnOrderStatusOptions,
          showForOptions: this.sectionShowForOptions
        },
        values: this.searchForm.value,
        resetPage: this.resetPage,
        getListMethod: (searchParams, sort, shouldCache) => this.getListMethod(searchParams, sort, shouldCache),
        sortKey: this.sortKey,
        activeTab: this.activeTab,
        amList: this.amList,
        isOnAccountsPage: this.isOnAccountsPage,
        accountSearchValues: this.valuesFormDialog,
      },
      autoFocus: false,
      panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
    });

    advancedFilteringDialog.afterClosed().subscribe(values => {
      if (this.deviceService.isMobile() && this.searchFieldMobile) {
        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement && activeElement.blur) {
            activeElement.blur();
          }
        }, 150);
      }

      this.parseAdvancedFilteringDialogValues(values);
    });
  }

  /**
   * Saves the non-null values from the pop-up form
   */
  private parseAdvancedFilteringDialogValues(values) {
    if (!values) {
      return;
    }

    if (values.clearAllFilters) {
      this.clearFilters();
      return;
    }

    const displayedValues = { ...values };

    for (const prop in displayedValues) {
      if (!displayedValues[prop]) {
        delete displayedValues[prop];
      }
    }
    this.valuesFormDialog = { ...displayedValues };

    displayedValues.accountManager = displayedValues.accountManager?.name;

    displayedValues.quotesShowFor = this.sectionShowForOptions.find(option => option.value === displayedValues.quotesShowFor)?.label;
    if (this.lnOrderStatusOptions && displayedValues.lnOrderStatus !== undefined) {
      displayedValues.lnOrderStatus = this.lnOrderStatusOptions.find(option => option.value === displayedValues.lnOrderStatus)?.label;
    }

    // Only include dates if both are present
    const hasStartDate = !!displayedValues.startDate;
    const hasEndDate = !!displayedValues.endDate;
    if (hasStartDate && hasEndDate) {
      displayedValues.startDate = this.touchpointsService.getDateLabel(displayedValues.startDate);
      displayedValues.endDate = this.touchpointsService.getDateLabel(displayedValues.endDate);
    } else {
      // Remove dates if only one is present
      delete displayedValues.startDate;
      delete displayedValues.endDate;
    }

    // Filter out undefined, null, and empty values, then convert to strings before joining
    const displayedText = Object.values(displayedValues)
      .filter(value => value !== undefined && value !== null && value !== '')
      .map(value => typeof value === 'object' ? JSON.stringify(value) : String(value))
      .join('; ');

    this.searchForm.controls.displayedSearchValue.setValue(displayedText);
    this.searchForm.markAsDirty();
    this.propagateAccountManagerSelection(values.accountManager);
    this.searchText();
  }

  /**
   * @param accountManager account manager returned by the advanced search dialog
   */
  private propagateAccountManagerSelection(accountManager: SelectedAccountManager) {
    if (this.isOnAccountsPage || this.appInfoService.isAccountManager() || !accountManager) {
      return;
    }

    if (accountManager.employeeId === this.allAM.employeeId) {
      this.selectedAccountManagerService.clear();
      return;
    }

    this.selectedAccountManagerService.select(
      this.amList?.find(am => am.employeeId === accountManager.employeeId) ?? accountManager
    );
  }

  private setFormOptions() {
    if (this.templateValuesType) {
      this.sectionShowForOptions = [];
      Object.keys(this.templateValuesType).forEach(filterVal => {
        this.sectionShowForOptions.push({
          value: Number(filterVal),
          label: (filterVal === '1') ? `${this.templateValuesType[filterVal]}` : `${this.templateValuesType[filterVal]} ${this.label}`
        });
      });
    }

    if (this.lnOrderTemplates?.LnOrderStatus) {
      this.lnOrderStatusOptions = [];
      Object.keys(this.lnOrderTemplates.LnOrderStatus).forEach(filterVal => {
        this.lnOrderStatusOptions.push({
          value: Number(filterVal),
          label: this.lnOrderTemplates.LnOrderStatus[filterVal]
        });
      });
    }
  }

  // Sets the default account manager and initializes inner form with it
  private initAccountManagerField(shouldClearFilters?: boolean) {
    if (!this.cachedSearchValues?.accountManager && this.appInfoService.isAccountManager() ||
      shouldClearFilters && this.appInfoService.isAccountManager()) {
      this.defaultAccountManager = {
        employeeId: this.appInfoService.userData.UserInfo.EmployeeId,
        name: this.appInfoService.userData.UserInfo.Name
      };
      // At refresh, the AM from the search form will be the one saved in sessionCache, before refreshing the page
    } else {
      this.defaultAccountManager = { ...this.allAM };
    }

    if (this.cachedSearchValues?.accountManager &&
      this.cachedSearchValues.accountManager.employeeId !== '0' && this.defaultAccountManager?.employeeId === '0'
      && !shouldClearFilters) {
      this.defaultAccountManager = {
        employeeId: this.cachedSearchValues?.accountManager.employeeId,
        name: this.cachedSearchValues?.accountManager.name
      };
    }

    this.valuesFormDialog.accountManager = { ...this.defaultAccountManager };
  }

  updateCalendarLabelStartDate(date) {
    this.searchForm.controls.startDate.setValue(new Date(date).toISOString());
    this.prettyStartDate = this.touchpointsService.getDateLabel(date);
    if (this.searchForm.controls.endDate.value !== null) {
      this.searchFromDropdownOrPicker();
    }
  }

  updateCalendarLabelEndDate(date) {
    this.searchForm.controls.endDate.setValue(new Date(date).toISOString());
    this.prettyEndDate = this.touchpointsService.getDateLabel(date);
    if (this.searchForm.controls.startDate.value > this.searchForm.controls.endDate.value) {
      this.updateCalendarLabelStartDate(date);
    }
    if (this.searchForm.controls.startDate.value !== null) {
      this.searchFromDropdownOrPicker();
    }
  }

  searchFromDropdownOrPicker() {
    this.clearFiltersNeeded = true;
    this.search(null, true);
  }

  private search(sort?: Sort, shouldCache?: boolean) {
    let searchParams = { ...this.searchForm.value, ...this.valuesFormDialog };

    searchParams = {
      ...searchParams,
      startDate: this.valuesFormDialog.startDate,
      endDate: this.valuesFormDialog.endDate,
      quotesShowFor: this.valuesFormDialog.quotesShowFor
    };

    if (!this.searchForm.dirty && !sort) {
      return;
    }
    // this resets the sort indicator
    this.resetSort.next(undefined);
    this.resetPageMethod();
    this.blockSearchOnBpForAccountsPage(searchParams);
    this.getListMethod(searchParams, sort, shouldCache && !this.isOnAccountsPage);
  }

  public resetPageMethod() {
    this.resetPage.emit(true);
  }

  /**
   * Searches from the search textbox
   */
  searchText() {
    this.currentSearchText = this.searchForm.value.displayedSearchValue;
    this.search(null, true);
  }

  /**
   * Clear all filters from the main form
   */
  clearFilters() {
    this.clearFiltersNeeded = false;
    this.currentSearchText = '';
    this.valuesFormDialog = {};
    this.initAccountManagerField(true);

    this.searchForm.patchValue({
      displayedSearchValue: '',
      quotesShowFor: 1,
      ordersShowFor: 1,
      lnOrderStatus: 0,
    });

    this.filteredAMList = this.amList;
    if (!this.isOnAccountsPage) {
      this.selectedAccountManagerService.clear();
    }
    sessionStorage.removeItem(StorageValues.CachedFilters + this.sortKey);
    sessionStorage.removeItem(StorageValues.CachedSort + this.sortKey);

    if (this.searchForm.controls.startDate.value !== null) {
      this.searchForm.patchValue({ startDate: null });
      this.prettyStartDate = this.prettyStartDateValue;
    }

    if (this.searchForm.controls.endDate.value !== null) {
      this.searchForm.patchValue({ endDate: null });
      this.prettyEndDate = this.prettyEndDateValue;
    }

    this.search();
  }

  handleNewQuote() {
    this.router.navigate(['/quotes/new']);
  }

  /**
   * Checks for cachedValues
   * Replaces the formValues with cachedValues
   */
  private getAndApplyStickyFilters() {
    this.cachedSearchValues = sessionStorage.getItem(StorageValues.CachedFilters + this.sortKey);
    this.cachedSort = sessionStorage.getItem(StorageValues.CachedSort + this.sortKey);
    this.cachedSearchValues = JSON.parse(this.cachedSearchValues) ?? this.getSelectedAccountManagerFilters();
    this.cachedSort = JSON.parse(this.cachedSort);
    if (this.cachedSearchValues) {
      this.searchForm.markAsDirty();
      this.searchForm.patchValue(this.cachedSearchValues);
      this.currentSearchText = this.cachedSearchValues.displayedSearchValue;
      this.valuesFormDialog = this.cachedSearchValues;
      this.clearFiltersNeeded = true;
    }

    if (this.cachedSort) {
      setTimeout(() => this.resetSort.next(this.cachedSort));
    }

    if (this.cachedSearchValues?.startDate) {
      this.searchForm.patchValue({ startDate: new Date(this.cachedSearchValues.startDate).toISOString() });
      this.prettyStartDate = this.touchpointsService.getDateLabel(this.cachedSearchValues.startDate);
    }

    if (this.cachedSearchValues?.endDate) {
      this.searchForm.patchValue({ endDate: new Date(this.cachedSearchValues.endDate).toISOString() });
      this.prettyEndDate = this.touchpointsService.getDateLabel(this.cachedSearchValues.endDate);
    }
  }

  private getSelectedAccountManagerFilters() {
    const accountManager = this.selectedAccountManagerService.selected;
    return accountManager && !this.appInfoService.isAccountManager()
      ? { accountManager, displayedSearchValue: accountManager.name }
      : null;
  }

  private initList() {
    if (!this.isOnAccountsPage) {
      this.getAndApplyStickyFilters();
    }
    this.initAccountManagerField();

    if (this.cachedSearchValues) {
      this.getListMethod(this.cachedSearchValues, this.cachedSort, true);
      return;
    }

    const fullFormValues = { ...this.valuesFormDialog, ...this.searchForm.value };

    this.blockSearchOnBpForAccountsPage(fullFormValues);
    this.getListMethod(fullFormValues, this.cachedSort, false);
  }

  private blockSearchOnBpForAccountsPage(searchValues: any) {
    // block the search on BP number when on accounts page
    if (this.isOnAccountsPage) {
      searchValues.bpNumber = this.bpNumber;
      if (
        String(this.activeTab) === String(this.activeTabName.Invoices) ||
        String(this.activeTab) === String(this.activeTabName.LNOrders) ||
        String(this.activeTab) === String(this.activeTabName.Orders)) {
        searchValues.accountManager = { ...searchValues.accountManager, employeeId: undefined };
      }
    }
  }

  private setupMobileToggleButtons() {
    this.mobileToggleValues = [{
      value: this.appStrings.menu_openQuotes
    }, {
      value: this.appStrings.menu_transactionHistory
      // TODO: add pages
      // }, {
      //   value: this.appStrings.menu_lnOrders
      // }, {
      //   value: this.appStrings.menu_Invoices
    }];
  }

  private setupToggleButtons() {
    this.toggleValues = [{
      value: this.appStrings.menu_openQuotes
    }, {
      value: this.appStrings.menu_transactionHistory
    }, {
      value: this.appStrings.menu_lnOrders
    }, {
      value: this.appStrings.menu_Invoices
    }];
  }
}
